import React, { useEffect, useMemo, useState } from "react";
import { HeartPulse, Users, AlertTriangle, Sparkles, CheckCircle2 } from "lucide-react";
import { HospitalHeader, BrandedShell, StatChip, BrandedNav } from "@/components/BrandedLayout";
import { PatientCard } from "@/components/PatientCard";
import { TriageView } from "@/components/TriageView";
import { WeeklyStatusCard } from "@/components/WeeklyStatusCard";
import { AddPatientDialog } from "@/components/AddPatientDialog";
import { AIEngineSelector } from "@/components/AIEngineSelector";
import { VoiceCaptureButton } from "@/components/VoiceCaptureButton";
import { FooterDisclaimer } from "@/components/FooterDisclaimer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function DoctorPortal() {
  const { hospCode } = useBranding();
  const [patients, setPatients] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [specialty, setSpecialty] = useState(hospCode === "fortis" ? "general_surgery" : "orthopedics");

  const load = async () => {
    const [ps, ws] = await Promise.all([api.listPatients(hospCode), api.weeklySummaries(hospCode)]);
    setPatients(ps);
    setSummaries(ws);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hospCode]);

  const specialtyPatients = useMemo(() => patients.filter((p) => p.specialty === specialty), [patients, specialty]);
  const specialtyIds = useMemo(() => new Set(specialtyPatients.map((p) => p.id)), [specialtyPatients]);
  const specialtySummaries = useMemo(() => summaries.filter((s) => specialtyIds.has(s.patient_id)), [summaries, specialtyIds]);

  // Sort weekly cards by priority: High-Risk → Needs Attention → Routine (unapproved first within Routine)
  const orderedSummaries = useMemo(() => {
    const rank = { "High-Risk": 0, "Needs Attention": 1, "Routine": 2 };
    return [...specialtySummaries].sort((a, b) => {
      const tr = rank[a.tier] - rank[b.tier];
      if (tr !== 0) return tr;
      // routines: unapproved first
      const aApproved = a.approved_at ? 1 : 0;
      const bApproved = b.approved_at ? 1 : 0;
      return aApproved - bApproved;
    });
  }, [specialtySummaries]);

  const highRisk = patients.filter((p) => p.risk_score > 60).length;
  const routineAwaitingApproval = specialtySummaries.filter((s) => s.tier === "Routine" && !s.approved_at).length;

  const onVoiceComplete = () => {
    toast.success("AI plan drafted", {
      description: `${specialty === "orthopedics" ? "Orthopedic AI" : "General Surgery AI"} · Ready to attach to a patient.`,
    });
  };

  return (
    <BrandedShell>
      <HospitalHeader subtitle="Surgeon portal · weekly focus" testId="doctor-header" />
      <BrandedNav role="doctor" />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 flex-wrap">
          <AIEngineSelector value={specialty} onChange={setSpecialty} />
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Sparkles className="w-3 h-3 brand-text" />
            Drives plan generation & daily questions
          </div>
        </div>
        <div className="flex items-end gap-6 flex-wrap">
          <VoiceCaptureButton
            onComplete={onVoiceComplete}
            label="Voice → Plan"
            helper="Tap · speak · draft plan"
            size="sm"
            testId="doctor-voice-btn"
          />
          <AddPatientDialog specialty={specialty} onCreated={load} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatChip icon={Users} label="Total Patients" value={patients.length} testId="stat-total" accent />
        <StatChip icon={HeartPulse} label="On This Engine" value={specialtyPatients.length} testId="stat-specialty" accent />
        <StatChip icon={AlertTriangle} label="High-Risk" value={highRisk} testId="stat-high-risk" />
        <StatChip icon={CheckCircle2} label="Awaiting Approval" value={routineAwaitingApproval} testId="stat-awaiting" />
      </div>

      <Tabs defaultValue="weekly">
        <TabsList className="rounded-full bg-secondary p-1 mb-6 border border-border">
          <TabsTrigger value="weekly" data-testid="tab-weekly"
            className="rounded-full data-[state=active]:bg-card data-[state=active]:shadow-sm px-4 text-xs">
            Weekly Status ({specialtySummaries.length})
          </TabsTrigger>
          <TabsTrigger value="patients" data-testid="tab-patients"
            className="rounded-full data-[state=active]:bg-card data-[state=active]:shadow-sm px-4 text-xs">
            All Patients ({specialtyPatients.length})
          </TabsTrigger>
          <TabsTrigger value="triage" data-testid="tab-triage"
            className="rounded-full data-[state=active]:bg-card data-[state=active]:shadow-sm px-4 text-xs">
            Triage View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="weekly">
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
            <div>
              AI-summarized 7-day check-ins. Glance-and-approve for
              <span className="font-medium text-emerald-600 dark:text-emerald-400"> Routine </span>·
              nurse manages
              <span className="font-medium text-amber-600 dark:text-amber-400"> Needs Attention </span>·
              surgeon priority for
              <span className="font-medium text-rose-600 dark:text-rose-400"> High-Risk</span>.
            </div>
          </div>
          {orderedSummaries.length === 0 ? (
            <div className="surface-card p-12 text-center text-muted-foreground">No summaries yet.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-in-up" data-testid="weekly-grid">
              {orderedSummaries.map((s) => (
                <WeeklyStatusCard key={s.patient_id} summary={s} onApproved={load} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="patients">
          {specialtyPatients.length === 0 ? (
            <div className="surface-card p-12 text-center">
              <div className="text-muted-foreground mb-3">
                No patients on {specialty === "orthopedics" ? "Orthopedic AI" : "General Surgery AI"} yet.
              </div>
              <AddPatientDialog specialty={specialty} onCreated={load} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in-up">
              {specialtyPatients.map((p) => <PatientCard key={p.id} patient={p} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="triage">
          <TriageView patients={patients} />
        </TabsContent>
      </Tabs>

      <FooterDisclaimer />
    </BrandedShell>
  );
}
